const TILE_SIZE = 256;
const MAX_LATITUDE = 85.05112878;
const DEFAULT_WIDTH = 1200;
const DEFAULT_HEIGHT = 620;

const LAYERS = [
  ["analysisArea", { stroke: "#9a6816", fill: "rgba(212, 166, 80, 0.10)", width: 4, dash: [12, 10] }],
  ["parcel", { stroke: "#17603f", fill: "rgba(91, 154, 117, 0.14)", width: 5 }],
  ["flood", { stroke: "#126aa1", fill: "rgba(39, 145, 201, 0.38)", width: 3 }],
  ["wetlands", { stroke: "#496f36", fill: "rgba(115, 155, 77, 0.42)", width: 3 }],
  ["buildings", { stroke: "#6d4f36", fill: "rgba(154, 122, 91, 0.28)", width: 2 }]
];

export function reportMapViewport(snapshot, { width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT, padding = 64 } = {}) {
  const address = addressCoordinate(snapshot);
  const geometry = snapshot?.map?.parcel || snapshot?.map?.analysisArea;
  const coordinates = geometryCoordinates(geometry);
  coordinates.push(address);

  const projected = coordinates.map(([longitude, latitude]) => project(longitude, latitude));
  const bounds = projected.reduce((result, point) => ({
    minX: Math.min(result.minX, point.x),
    maxX: Math.max(result.maxX, point.x),
    minY: Math.min(result.minY, point.y),
    maxY: Math.max(result.maxY, point.y)
  }), { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity });

  let zoom = 18;
  while (zoom > 2) {
    const scale = 2 ** zoom;
    const fitsWidth = (bounds.maxX - bounds.minX) * scale <= width - padding * 2;
    const fitsHeight = (bounds.maxY - bounds.minY) * scale <= height - padding * 2;
    if (fitsWidth && fitsHeight) break;
    zoom -= 1;
  }

  const addressWorld = scalePoint(project(...address), zoom);
  const centerWorld = snapshot?.analysisMode === "address_area"
    ? addressWorld
    : {
        x: (bounds.minX + bounds.maxX) / 2 * 2 ** zoom,
        y: (bounds.minY + bounds.maxY) / 2 * 2 ** zoom
      };
  const topLeft = { x: centerWorld.x - width / 2, y: centerWorld.y - height / 2 };

  return {
    width,
    height,
    zoom,
    topLeft,
    addressPixel: { x: addressWorld.x - topLeft.x, y: addressWorld.y - topLeft.y }
  };
}

export async function createReportMapImage(snapshot, {
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  tileLoader = loadTile,
  canvasFactory = browserCanvas,
  pixelRatio = Math.min(globalThis.devicePixelRatio || 1, 2)
} = {}) {
  if (!canvasFactory) return null;
  const canvas = canvasFactory(width * pixelRatio, height * pixelRatio);
  if (!canvas) return null;
  const viewport = reportMapViewport(snapshot, { width, height });
  canvas.width = width * pixelRatio;
  canvas.height = height * pixelRatio;
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.scale(pixelRatio, pixelRatio);
  context.fillStyle = "#eef2ef";
  context.fillRect(0, 0, width, height);

  await drawTiles(context, viewport, tileLoader);
  for (const [key, style] of LAYERS) drawGeoJson(context, snapshot?.map?.[key], viewport, style);
  drawAddressPoint(context, viewport.addressPixel);
  drawMapLabels(context, viewport, snapshot);
  return canvas.toDataURL("image/jpeg", 0.92);
}

function browserCanvas(width, height) {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

async function drawTiles(context, viewport, tileLoader) {
  const tileCount = 2 ** viewport.zoom;
  const firstX = Math.floor(viewport.topLeft.x / TILE_SIZE);
  const lastX = Math.floor((viewport.topLeft.x + viewport.width) / TILE_SIZE);
  const firstY = Math.max(0, Math.floor(viewport.topLeft.y / TILE_SIZE));
  const lastY = Math.min(tileCount - 1, Math.floor((viewport.topLeft.y + viewport.height) / TILE_SIZE));
  const requests = [];

  for (let x = firstX; x <= lastX; x += 1) {
    for (let y = firstY; y <= lastY; y += 1) {
      const wrappedX = ((x % tileCount) + tileCount) % tileCount;
      const url = `https://tile.openstreetmap.org/${viewport.zoom}/${wrappedX}/${y}.png`;
      requests.push(tileLoader(url).then(image => ({ image, x, y })).catch(() => null));
    }
  }

  const tiles = await Promise.all(requests);
  for (const tile of tiles.filter(Boolean)) {
    context.drawImage(
      tile.image,
      tile.x * TILE_SIZE - viewport.topLeft.x,
      tile.y * TILE_SIZE - viewport.topLeft.y,
      TILE_SIZE,
      TILE_SIZE
    );
  }
}

function loadTile(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const timeout = setTimeout(() => reject(new Error("Map tile timed out.")), 8000);
    image.crossOrigin = "anonymous";
    image.onload = () => {
      clearTimeout(timeout);
      resolve(image);
    };
    image.onerror = () => {
      clearTimeout(timeout);
      reject(new Error("Map tile failed."));
    };
    image.src = url;
  });
}

function drawGeoJson(context, geojson, viewport, style) {
  const features = geojson?.type === "FeatureCollection" ? geojson.features : geojson ? [geojson] : [];
  for (const feature of features) drawGeometry(context, feature?.geometry || feature, viewport, style);
}

function drawGeometry(context, geometry, viewport, style) {
  if (!geometry?.coordinates) return;
  const type = geometry.type;
  const polygons = type === "Polygon" ? [geometry.coordinates] : type === "MultiPolygon" ? geometry.coordinates : [];
  const lines = type === "LineString" ? [geometry.coordinates] : type === "MultiLineString" ? geometry.coordinates : [];

  if (polygons.length) {
    context.save();
    context.beginPath();
    for (const polygon of polygons) for (const ring of polygon) traceLine(context, ring, viewport, true);
    context.fillStyle = style.fill;
    context.fill("evenodd");
    context.strokeStyle = style.stroke;
    context.lineWidth = style.width;
    context.setLineDash(style.dash || []);
    context.stroke();
    context.restore();
  }

  for (const line of lines) {
    context.save();
    context.beginPath();
    traceLine(context, line, viewport, false);
    context.strokeStyle = style.stroke;
    context.lineWidth = style.width;
    context.setLineDash(style.dash || []);
    context.stroke();
    context.restore();
  }
}

function traceLine(context, coordinates, viewport, close) {
  coordinates.forEach((coordinate, index) => {
    const pixel = mapPixel(coordinate, viewport);
    if (index === 0) context.moveTo(pixel.x, pixel.y);
    else context.lineTo(pixel.x, pixel.y);
  });
  if (close) context.closePath();
}

function drawAddressPoint(context, point) {
  context.save();
  context.beginPath();
  context.arc(point.x, point.y, 10, 0, Math.PI * 2);
  context.fillStyle = "#15241d";
  context.fill();
  context.lineWidth = 5;
  context.strokeStyle = "#ffffff";
  context.stroke();
  context.restore();
}

function drawMapLabels(context, viewport, snapshot) {
  const label = snapshot?.analysisMode === "verified_parcel" ? "Matched address within returned parcel" : "Matched address - center of 1,000-foot screen";
  context.save();
  context.font = "600 22px system-ui, sans-serif";
  const labelWidth = context.measureText(label).width;
  context.fillStyle = "rgba(255, 255, 255, 0.94)";
  context.fillRect(18, 18, labelWidth + 44, 42);
  context.fillStyle = "#173f2d";
  context.fillText(label, 40, 47);

  const attribution = "© OpenStreetMap contributors";
  context.font = "18px system-ui, sans-serif";
  const attributionWidth = context.measureText(attribution).width;
  context.fillStyle = "rgba(255, 255, 255, 0.92)";
  context.fillRect(viewport.width - attributionWidth - 32, viewport.height - 34, attributionWidth + 32, 34);
  context.fillStyle = "#45534c";
  context.fillText(attribution, viewport.width - attributionWidth - 16, viewport.height - 11);
  context.restore();
}

function mapPixel(coordinate, viewport) {
  const world = scalePoint(project(coordinate[0], coordinate[1]), viewport.zoom);
  return { x: world.x - viewport.topLeft.x, y: world.y - viewport.topLeft.y };
}

function addressCoordinate(snapshot) {
  const longitude = Number(snapshot?.property?.longitude);
  const latitude = Number(snapshot?.property?.latitude);
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) throw new Error("The matched address coordinate is missing.");
  return [longitude, latitude];
}

function geometryCoordinates(geojson) {
  const output = [];
  const visit = value => {
    if (!Array.isArray(value)) return;
    if (value.length >= 2 && Number.isFinite(Number(value[0])) && Number.isFinite(Number(value[1]))) {
      output.push([Number(value[0]), Number(value[1])]);
      return;
    }
    value.forEach(visit);
  };
  if (geojson?.type === "FeatureCollection") geojson.features.forEach(feature => visit(feature?.geometry?.coordinates));
  else visit((geojson?.geometry || geojson)?.coordinates);
  return output;
}

function project(longitude, latitude) {
  const limitedLatitude = Math.max(-MAX_LATITUDE, Math.min(MAX_LATITUDE, Number(latitude)));
  const sin = Math.sin(limitedLatitude * Math.PI / 180);
  return {
    x: (Number(longitude) + 180) / 360 * TILE_SIZE,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * TILE_SIZE
  };
}

function scalePoint(point, zoom) {
  const scale = 2 ** zoom;
  return { x: point.x * scale, y: point.y * scale };
}
